import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Download, Filter, Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { CustomerLanguageSelector } from '@/components/CustomerLanguageSelector';
import { DateRangePicker } from '@/components/DateRangePicker';
import { FormatSelector } from '@/components/FormatSelector';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/RadioGroup';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useCsvList } from '@/hooks/useCsvList';
import { useDownloadProductSeoCSV } from '@/hooks/useDownloadProductSeoCSV';
import i18n from '@/lib/i18n';
import { Format } from '@/services/ProductsService';

const schema = z.object({
  language: z
    .string()
    .min(
      1,
      i18n.t(
        'pages.SeoContent.components.DownloadButton.validation.languageRequired',
      ),
    ),
  format: z.enum([Format.CSV, Format.XLSX], {
    required_error: i18n.t(
      'pages.SeoContent.components.DownloadButton.validation.formatRequired',
    ),
  }),
  csvFileKey: z.string().optional(),
  dateFrom: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export function DownloadSelector() {
  const [url, setUrl] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType] = useState<'csv' | 'date'>('date');

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.SeoContent.components.DownloadButton',
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid, isSubmitting, errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      format: Format.CSV,
      csvFileKey: undefined,
    },
  });

  const languageValue = watch('language');

  const isLanguageSelected = Boolean(languageValue);

  const { downloadProductSeoCSV } = useDownloadProductSeoCSV();
  const { csvList, isLoading: isCsvListLoading } = useCsvList();
  const isCsvSelectDisabled =
    isSubmitting || isCsvListLoading || csvList.length === 0;

  function handleFilterTypeChange(type: 'csv' | 'date') {
    setFilterType(type);
    if (type === 'csv') {
      setValue('dateFrom', undefined, { shouldValidate: true });
    } else {
      setValue('csvFileKey', undefined, { shouldValidate: true });
    }
  }

  function handleToggleFilters() {
    setShowFilters((state) => !state);
  }

  function handleDownload() {
    if (url) {
      window.open(url);
      setUrl(null);
    }
  }

  function handleClearCsvFilter() {
    setValue('csvFileKey', undefined, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('dateFrom', undefined, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setFilterType('date');
    setShowFilters(false);
  }

  const onSubmit = handleSubmit(
    async ({ language, format, csvFileKey, dateFrom }) => {
      try {
        const data = await downloadProductSeoCSV({
          language,
          format,
          dateFrom,
          ...(csvFileKey ? { csvFileKey } : {}),
        });

        if (data.url) {
          setUrl(data.url);
        }
      } catch {
        toast.error(t('downloadError'));
      }
    },
  );

  return (
    <div className="flex justify-end">
      <form
        className="flex w-full flex-col items-end gap-3"
        onSubmit={onSubmit}
      >
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="w-[160px]">
            <Controller
              control={control}
              name="language"
              render={({ field }) => (
                <CustomerLanguageSelector
                  disabled={isSubmitting}
                  value={field.value}
                  onValueChange={field.onChange}
                />
              )}
            />
          </div>

          {isLanguageSelected && (
            <div className="w-[160px]">
              <Controller
                control={control}
                name="format"
                render={({ field }) => (
                  <FormatSelector
                    disabled={isSubmitting}
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                )}
              />
            </div>
          )}

          {isLanguageSelected && !showFilters && (
            <Button
              type="button"
              variant="outline"
              className="w-[160px] font-normal"
              onClick={handleToggleFilters}
            >
              <Filter className="h-4 w-4" /> {t('showCsvFilter')}
            </Button>
          )}

          {!isSubmitting && !url && (
            <Button type="submit" disabled={!isValid} className="w-[160px]">
              <Download className="h-4 w-4" /> {t('downloadLabelGenerate')}
            </Button>
          )}

          {!isSubmitting && url && (
            <Button
              type="button"
              className="w-[160px] bg-green-600 hover:bg-green-700"
              onClick={handleDownload}
            >
              <Check className="h-4 w-4" /> {t('downloadLabelReady')}
            </Button>
          )}

          {isSubmitting && (
            <Button type="button" className="w-[160px]">
              <Loader2 className="animate-spin" />
            </Button>
          )}
        </div>

        {showFilters && (
          <div className="flex w-[498px] flex-wrap items-center gap-2">
            <div className="ml-2">
              <RadioGroup
                value={filterType}
                onValueChange={(value) =>
                  handleFilterTypeChange(value as 'csv' | 'date')
                }
                className="flex flex-row gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="date" id="filter-date" />
                  <Label htmlFor="filter-date" className="cursor-pointer">
                    {t('filterByDate')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="csv" id="filter-csv" />
                  <Label htmlFor="filter-csv" className="cursor-pointer">
                    {t('filterByCsv')}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {filterType === 'csv' && (
              <div className="w-[300px]">
                <Controller
                  control={control}
                  name="csvFileKey"
                  render={({ field }) => (
                    <Select
                      disabled={isCsvSelectDisabled}
                      value={field.value || ''}
                      onValueChange={(value) =>
                        field.onChange(value || undefined)
                      }
                    >
                      <SelectTrigger
                        id="csv-file-filter"
                        className="w-full text-left"
                      >
                        <SelectValue placeholder={t('csvFilterPlaceholder')} />
                      </SelectTrigger>

                      <SelectContent>
                        {csvList.map((csv) => (
                          <SelectItem key={csv.fileKey} value={csv.fileKey}>
                            {csv.originalName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            )}

            {filterType === 'date' && (
              <div className="flex flex-row items-center gap-2">
                <h3 className="ml-2 text-sm text-gray-600">
                  {t('dateFromLabel')}
                </h3>
                <Controller
                  control={control}
                  name="dateFrom"
                  render={({ field }) => (
                    <DateRangePicker
                      dateFrom={field.value}
                      onDateFromChange={field.onChange}
                      error={errors.dateFrom?.message}
                    />
                  )}
                />
              </div>
            )}

            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={handleClearCsvFilter}
              aria-label={t('clearCsvFilter')}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
